import { applyOperationPolicy } from "./operation-policy";

const HTTP_METHODS = new Set([
	"get",
	"put",
	"post",
	"delete",
	"options",
	"head",
	"patch",
	"trace",
]);
const HIDDEN_PARAMS = new Set(["api-version-date"]);
export const ACCOUNT_FIELDS = ["company_id", "account_id"];
const AUTO_FILLED_PARAMS = new Set(ACCOUNT_FIELDS);

function hiddenParamRefs(spec: Record<string, unknown>): Set<string> {
	const refs = new Set<string>();
	const components = (spec.components as Record<string, unknown> | undefined)
		?.parameters;
	if (components && typeof components === "object") {
		for (const [key, definition] of Object.entries(
			components as Record<string, unknown>,
		)) {
			const name = (definition as Record<string, unknown> | null)?.name;
			if (typeof name === "string" && HIDDEN_PARAMS.has(name.toLowerCase())) {
				refs.add(`#/components/parameters/${key}`);
			}
		}
	}
	return refs;
}

function isHiddenParam(parameter: unknown, hiddenRefs: Set<string>): boolean {
	if (parameter == null || typeof parameter !== "object") return false;
	const record = parameter as Record<string, unknown>;
	if (typeof record.$ref === "string" && hiddenRefs.has(record.$ref)) {
		return true;
	}
	return (
		typeof record.name === "string" &&
		HIDDEN_PARAMS.has(record.name.toLowerCase())
	);
}

function transformParams(
	operation: Record<string, unknown>,
	hiddenRefs: Set<string>,
): Record<string, unknown> {
	const params = operation.parameters;
	if (!Array.isArray(params)) return operation;
	const relaxed = params
		.filter((parameter: unknown) => !isHiddenParam(parameter, hiddenRefs))
		.map((parameter: unknown) => {
			if (
				parameter != null &&
				typeof parameter === "object" &&
				AUTO_FILLED_PARAMS.has(
					String((parameter as Record<string, unknown>).name ?? ""),
				)
			) {
				const record = parameter as Record<string, unknown>;
				if (record.in === "path") {
					return { ...record, in: "query", required: false };
				}
				return { ...record, required: false };
			}
			return parameter;
		});
	return { ...operation, parameters: relaxed };
}

function transformNullableObjectBodyFields(
	operation: Record<string, unknown>,
): Record<string, unknown> {
	const requestBody = operation.requestBody as
		| {
				content?: Record<
					string,
					{ schema?: { properties?: Record<string, Record<string, unknown>> } }
				>;
		  }
		| undefined;
	const mediaType = requestBody?.content?.["application/json"];
	const schema = mediaType?.schema;
	if (!schema?.properties) return operation;

	let changed = false;
	const properties = Object.fromEntries(
		Object.entries(schema.properties).map(([name, property]) => {
			const types = Array.isArray(property.type) ? property.type : [];
			if (!types.includes("object") || !types.includes("null")) {
				return [name, property];
			}

			changed = true;
			const { type: _type, description, ...objectSchema } = property;
			return [
				name,
				{
					...(description ? { description } : {}),
					anyOf: [
						{ ...objectSchema, type: "object" },
						{ type: "null" },
						{ type: "string", enum: ["null"] },
					],
				},
			];
		}),
	);
	if (!changed) return operation;

	return {
		...operation,
		requestBody: {
			...requestBody,
			content: {
				...requestBody?.content,
				"application/json": {
					...mediaType,
					schema: { ...schema, properties },
				},
			},
		},
	};
}

export function filterSpecByTag(
	spec: Record<string, unknown>,
	tag: string,
): Record<string, unknown> {
	const hiddenRefs = hiddenParamRefs(spec);
	const paths: Record<string, Record<string, unknown>> = {};
	for (const [path, methods] of Object.entries(
		spec.paths as Record<string, Record<string, unknown>>,
	)) {
		const filtered: Record<string, unknown> = {};
		const nonOperations: [string, unknown][] = [];
		for (const [method, operation] of Object.entries(methods)) {
			if (!HTTP_METHODS.has(method)) {
				nonOperations.push([method, operation]);
				continue;
			}
			const record = operation as Record<string, unknown>;
			const tags = record.tags;
			if (Array.isArray(tags) && tags.includes(tag)) {
				filtered[method] = applyOperationPolicy(
					spec,
					method,
					path,
					transformNullableObjectBodyFields(
						transformParams(record, hiddenRefs),
					),
				);
			}
		}
		if (Object.keys(filtered).length > 0) {
			for (const [key, value] of nonOperations) filtered[key] = value;
			paths[path] = filtered;
		}
	}
	return { ...spec, paths };
}

/**
 * Path params are interpolated into request URLs verbatim, so an argv value
 * containing `/`, `?`, `#`, `\`, `%` or a control character — or being exactly
 * `.` or `..` — rewrites the request path once the URL parser normalizes it
 * (`--id 'x/../../admin'`, `--id '%2e%2e'` and `--id $'.\t.'` all land on a
 * different endpoint) and retargets the API call with the caller's credentials.
 * A path param can only ever be one URL segment, so constrain every plain
 * string path-param schema to a single segment the parser cannot rewrite;
 * incur's generated zod schema then rejects hostile
 * argv client-side, before any request is built. sync-spec re-bakes these JSON
 * files, so the constraint is applied where the specs are loaded instead of
 * editing the baked JSON.
 */
// Everything the WHATWG URL parser would turn into a different path: `/`, `?`,
// `#`, a `\` (treated as `/` on https), a `%` (percent-encoded dots decode and
// then normalize: %2e%2e -> ..), C0 controls (tab, LF and CR are stripped, so
// ".\t." becomes ".."), and a bare `.` or `..`. Kept under incur's 100-char
// compaction limit so the constraint survives spec loading.
export const PATH_PARAM_PATTERN = "^(?!\\.\\.?$)[^/?#%\\\\\\x00-\\x1f\\x7f]+$";

export function constrainPathParams(
	spec: Record<string, unknown>,
): Record<string, unknown> {
	const paths = spec.paths;
	if (!paths || typeof paths !== "object") return spec;
	for (const methods of Object.values(
		paths as Record<string, Record<string, unknown>>,
	)) {
		if (!methods || typeof methods !== "object") continue;
		for (const operation of Object.values(methods)) {
			if (!operation || typeof operation !== "object") continue;
			const parameters = (operation as Record<string, unknown>).parameters;
			if (!Array.isArray(parameters)) continue;
			for (const parameter of parameters) {
				if (parameter == null || typeof parameter !== "object") continue;
				const record = parameter as Record<string, unknown>;
				if (record.in !== "path") continue;
				const schema = record.schema;
				if (schema == null || typeof schema !== "object") continue;
				const constraint = schema as Record<string, unknown>;
				// Only plain string schemas: enums and compositions are already
				// constrained, and anything else keeps the spec's own shape.
				if (constraint.type !== "string") continue;
				if (
					constraint.pattern !== undefined ||
					Array.isArray(constraint.enum) ||
					constraint.anyOf !== undefined ||
					constraint.oneOf !== undefined ||
					constraint.allOf !== undefined
				) {
					continue;
				}
				constraint.pattern = PATH_PARAM_PATTERN;
			}
		}
	}
	return spec;
}
